import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Repeat, Link2, Lock } from 'lucide-react';
import { useSubscription } from '@/hooks/use-subscription';
import { supabase } from '@/integrations/supabase/client';
import { BudgetItem } from '@/hooks/use-annual-budget';
import { RecurringSeries } from '@/hooks/use-recurring-transactions';
import { CategorySelect } from '@/components/shared/CategorySelect';

const MONTHS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

const formSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  item_type: z.enum(['expense', 'income']),
  category_id: z.string().optional(),
  goal_id: z.string().optional(),
  recurrence_id: z.string().optional(),
  frequency: z.enum(['monthly', 'specific_months', 'annual']),
  active_months: z.array(z.number()).min(1, 'Selecione pelo menos um mês'),
  monthly_amount: z.coerce.number().min(0),
  is_fixed: z.boolean(),
  provision_enabled: z.boolean(),
  provision_months: z.coerce.number().min(1).max(12),
  auto_create_goal: z.boolean(),
  link_recurring: z.boolean(),
  auto_create_recurring: z.boolean(),
  account_id: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface BudgetItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: BudgetItem | null;
  onSave: (data: Partial<BudgetItem>) => void;
  recurringSeries?: RecurringSeries[];
}

export function BudgetItemModal({
  open,
  onOpenChange,
  item,
  onSave,
  recurringSeries = [],
}: BudgetItemModalProps) {
  const [goals, setGoals] = useState<{ id: string; name: string }[]>([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const { canAccess } = useSubscription();
  const provisionLocked = !canAccess('provisions');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      item_type: 'expense',
      category_id: '',
      goal_id: '',
      recurrence_id: '',
      frequency: 'monthly',
      active_months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      monthly_amount: 0,
      is_fixed: false,
      provision_enabled: false,
      provision_months: 12,
      auto_create_goal: true,
      link_recurring: false,
      auto_create_recurring: false,
      account_id: '',
      notes: '',
    },
  });

  const frequency = form.watch('frequency');
  const provisionEnabled = form.watch('provision_enabled');
  const goalId = form.watch('goal_id');
  const linkRecurring = form.watch('link_recurring');
  const autoCreateRecurring = form.watch('auto_create_recurring');
  const recurrenceId = form.watch('recurrence_id');
  const categoryId = form.watch('category_id');
  const itemType = form.watch('item_type');

  // Filter recurring series by matching category if available
  const filteredSeries = recurringSeries.filter(s => {
    if (itemType === 'income') return s.type === 'income';
    return s.type === 'expense';
  });

  useEffect(() => {
    const fetchData = async () => {
      const [goalsRes, accountsRes] = await Promise.all([
        supabase.from('goals').select('id, name').eq('is_active', true).order('name'),
        supabase.from('accounts').select('id, name').eq('archived', false).order('name'),
      ]);
      setGoals(goalsRes.data || []);
      const fetchedAccounts = accountsRes.data || [];
      setAccounts(fetchedAccounts);
      // Auto-select if only 1 account
      if (fetchedAccounts.length === 1 && !form.getValues('account_id')) {
        form.setValue('account_id', fetchedAccounts[0].id);
      }
    };

    if (open) fetchData();
  }, [open]);

  useEffect(() => {
    if (item) {
      form.reset({
        name: item.name,
        item_type: item.item_type,
        category_id: item.category_id || '',
        goal_id: item.goal_id || '',
        recurrence_id: item.recurrence_id || '',
        frequency: item.frequency,
        active_months: item.active_months,
        monthly_amount: item.monthly_amount,
        is_fixed: item.is_fixed,
        provision_enabled: item.provision_enabled,
        provision_months: item.provision_months,
        auto_create_goal: false,
        link_recurring: !!item.recurrence_id,
        auto_create_recurring: false,
        account_id: '',
        notes: item.notes || '',
      });
    } else {
      form.reset({
        name: '',
        item_type: 'expense',
        category_id: '',
        goal_id: '',
        recurrence_id: '',
        frequency: 'monthly',
        active_months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        monthly_amount: 0,
        is_fixed: false,
        provision_enabled: false,
        provision_months: 12,
        auto_create_goal: true,
        link_recurring: false,
        auto_create_recurring: false,
        account_id: '',
        notes: '',
      });
    }
  }, [item, open]);

  const handleSubmit = (values: FormValues) => {
    const saveData: any = {
      ...values,
      category_id: values.category_id || null,
      goal_id: values.goal_id || null,
      recurrence_id: values.link_recurring && values.recurrence_id ? values.recurrence_id : null,
      auto_create_goal: values.auto_create_goal && values.provision_enabled && !values.goal_id,
      auto_create_recurring: values.link_recurring && values.auto_create_recurring && !values.recurrence_id,
      account_id: values.account_id || undefined,
    };

    // Clean up form-only fields
    delete saveData.link_recurring;

    onSave(saveData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? 'Editar Item' : 'Novo Item do Orçamento'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: IPVA, Material Escolar" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="item_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="expense">Despesa</SelectItem>
                        <SelectItem value="income">Receita</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <FormControl>
                      <CategorySelect
                        value={field.value || null}
                        onChange={(id) => field.onChange(id)}
                        type={form.watch('item_type') as 'income' | 'expense'}
                        placeholder="Selecione..."
                        allowCreate={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Frequência</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="monthly">Mensal (mesmo valor)</SelectItem>
                      <SelectItem value="specific_months">Meses específicos</SelectItem>
                      <SelectItem value="annual">Anual (pagamento único)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {frequency === 'monthly' && 'O valor será repetido em todos os meses selecionados'}
                    {frequency === 'specific_months' && 'Escolha os meses em que a despesa ocorre'}
                    {frequency === 'annual' && 'Pagamento único no primeiro mês selecionado'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {frequency !== 'monthly' && (
              <FormField
                control={form.control}
                name="active_months"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meses</FormLabel>
                    <div className="grid grid-cols-4 gap-2">
                      {MONTHS.map(month => (
                        <label
                          key={month.value}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                        >
                          <Checkbox
                            checked={field.value.includes(month.value)}
                            onCheckedChange={(checked) => {
                              const newValue = checked
                                ? [...field.value, month.value].sort((a, b) => a - b)
                                : field.value.filter(v => v !== month.value);
                              field.onChange(newValue);
                            }}
                          />
                          {month.label.slice(0, 3)}
                        </label>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="monthly_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {frequency === 'annual' ? 'Valor Total' : 'Valor Mensal'}
                  </FormLabel>
                  <FormControl>
                    <Input type="number" step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_fixed"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel>Despesa Fixa</FormLabel>
                    <FormDescription>
                      Valor não varia mês a mês
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="provision_enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel className="flex items-center gap-2">
                      Habilitar Provisão
                      {provisionLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </FormLabel>
                    <FormDescription>
                      {provisionLocked
                        ? 'Disponível no plano Starter'
                        : 'Poupar mensalmente para esta despesa'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={provisionLocked}
                            />
                          </div>
                        </TooltipTrigger>
                        {provisionLocked && (
                          <TooltipContent>
                            Disponível no plano Starter
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </FormControl>
                </FormItem>
              )}
            />

            {provisionEnabled && (
              <>
                <FormField
                  control={form.control}
                  name="provision_months"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meses para Provisionar</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} max={12} {...field} />
                      </FormControl>
                      <FormDescription>
                        Quantos meses antes começar a guardar
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="goal_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cofrinho Vinculado</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um cofrinho..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {goals.map(goal => (
                            <SelectItem key={goal.id} value={goal.id}>
                              {goal.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Selecione um cofrinho existente ou crie automaticamente
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!goalId && (
                  <FormField
                    control={form.control}
                    name="auto_create_goal"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-3">
                        <div>
                          <FormLabel className="text-primary">Criar Cofrinho Automaticamente</FormLabel>
                          <FormDescription>
                            Um cofrinho será criado com o mesmo nome e valor desta despesa
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}

            {/* Recurring Linking Section */}
            {frequency === 'monthly' && (
              <>
                <FormField
                  control={form.control}
                  name="link_recurring"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <FormLabel>Vincular a Transações Recorrentes</FormLabel>
                          <FormDescription>
                            Conectar a uma série de transações recorrentes para rastreamento preciso
                          </FormDescription>
                        </div>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {linkRecurring && (
                  <>
                    <FormField
                      control={form.control}
                      name="recurrence_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Série Recorrente</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione uma série existente..." />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {filteredSeries.map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                  <div className="flex items-center gap-2">
                                    <Repeat className="h-3 w-3" />
                                    <span>{s.description}</span>
                                    <Badge variant="outline" className="text-xs ml-1">
                                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(s.amount)}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Vincule a uma série já existente em Recorrência
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {!recurrenceId && (
                      <>
                        <FormField
                          control={form.control}
                          name="auto_create_recurring"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                              <div>
                                <FormLabel className="text-emerald-600">Criar Série Automaticamente</FormLabel>
                                <FormDescription>
                                  12 transações mensais serão geradas com o valor e categoria deste item
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {autoCreateRecurring && (
                          <FormField
                            control={form.control}
                            name="account_id"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Conta Bancária</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Selecione a conta..." />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {accounts.map(acc => (
                                      <SelectItem key={acc.id} value={acc.id}>
                                        {acc.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormDescription>
                                  Conta onde as transações serão lançadas
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notas adicionais..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {item ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
